import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, WifiOff, RefreshCw, Pencil, Megaphone, MegaphoneOff, Search } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import LoadError from "@/components/dashboard/LoadError";
import ExpandableText from "@/components/ui/ExpandableText";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useApiData } from "@/hooks/useApiData";
import { listChangelog, createChangelog, updateChangelogEntry, deleteChangelogEntry } from "@/api/admin";
import { fieldErrors } from "@/api/client";
import { toastError, toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { categoryTone, CATEGORIES, AUDIENCES, audienceLabel, countLines, MAX_BODY_LINES } from "@/lib/changelog";
import { formatDate } from "@/lib/dates";

const DEMO = { entries: [{ id: "e1", version: "v1.4", title: "AI Firewall", body: "Redact secrets before they reach the model.", category: "feature", audience: "all", published: true }] };
const BLANK = { version: "", title: "", body: "", category: "feature", audience: "all", published: false };
const PAGE_SIZE = 10;
const fmtDate = (v) => formatDate(v, "");
const STATE_FILTERS = [
  { key: "all", label: "All" },
  { key: "published", label: "Published" },
  { key: "draft", label: "Drafts" },
];

// These mirror admin_platform.py exactly — MAX_CHANGELOG_TITLE,
// MAX_CHANGELOG_BODY_LINES and MAX_CHANGELOG_BODY. Enforced on BOTH sides on
// purpose: the form explains the limit while the author is typing, the server
// decides. Neither field is capped with `maxLength`, because clipping a paste
// at the limit is exactly the silent truncation an author would not notice —
// the text stays as typed and the form says why it can't be saved.
const MAX_TITLE = 40;
// Mirrors MAX_CHANGELOG_BODY, which the API derives from the line limit so the
// two rules cannot contradict each other — a valid 300-line description must
// never be refused by a character cap.
const MAX_BODY = MAX_BODY_LINES * 400;

// The <select> and <textarea> here are native elements, so they don't inherit
// anything from the Input component. These two strings reproduce Input's own
// label and control classes exactly, which is what keeps the Version box, the
// Category picker and the Title box on one visual grid — they were previously
// h-11/rounded-xl next to py-2/rounded-lg, so every row sat at a different
// height with mismatched corners.
const FIELD_LABEL = "mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300";
const FIELD_BASE =
  "w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500";
const SELECT_CLASS = cn(FIELD_BASE, "h-11");
const TEXTAREA_CLASS = cn(FIELD_BASE, "py-3 leading-relaxed");

// The star is rendered by Input for its own fields; the native-element labels
// below need the same marker, and it must appear on exactly the fields
// `validate()` actually rejects when empty — a decorative star on an optional
// field is worse than none.
function RequiredMark() {
  return <span className="text-rose-500" aria-hidden="true"> *</span>;
}

// One definition of "valid", used by the Save button, by the inline messages and
// by nothing else — so the form can never claim a field is fine and then be
// refused by the server for that same field.
function validate(form) {
  const errors = {};
  const title = (form.title || "").trim();
  // The body is measured RAW — the same string that is sent, and the same one
  // the server counts. Trimming it here would hide leading and trailing blank
  // lines from the count while the server still refused them, which is the two
  // sides disagreeing at exactly the boundary the author is standing on.
  const rawBody = form.body || "";
  if (!title) errors.title = "Give this update a title.";
  else if (title.length > MAX_TITLE) errors.title = `Title must not exceed ${MAX_TITLE} characters.`;
  if (!rawBody.trim()) errors.body = "Describe what changed.";
  else if (countLines(rawBody) > MAX_BODY_LINES) errors.body = `Description must not exceed ${MAX_BODY_LINES} lines.`;
  else if (rawBody.length > MAX_BODY) errors.body = `Keep the description under ${MAX_BODY.toLocaleString()} characters.`;
  return errors;
}

export default function Changelog() {
  const { data, loading, demo, error, reload } = useApiData(listChangelog, DEMO);
  const rows = useMemo(() => (data && data.entries) || [], [data]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  // The server said this looks like an existing update. Holds the message it
  // sent, and drives the "post anyway?" step — see save().
  const [pendingDuplicate, setPendingDuplicate] = useState(null);
  // A published entry is user-facing, so deleting the wrong one is visible
  // outside the admin surface. Confirm before the DELETE goes out.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingPublish, setPendingPublish] = useState(null);
  // The id being published/unpublished, so only THAT row's control shows a
  // spinner and goes disabled — a single shared flag froze every row's button.
  const [publishingId, setPublishingId] = useState(null);
  const [query, setQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);

  const stateCounts = useMemo(() => ({
    all: rows.length,
    published: rows.filter((e) => e.published).length,
    draft: rows.filter((e) => !e.published).length,
  }), [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((e) => {
      if (stateFilter === "published" && !e.published) return false;
      if (stateFilter === "draft" && e.published) return false;
      if (!q) return true;
      return `${e.title || ""} ${e.body || ""} ${e.version || ""}`.toLowerCase().includes(q);
    });
  }, [rows, query, stateFilter]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  function open(e) {
    setEditing(e || {});
    setForm(e ? { ...BLANK, ...e } : { ...BLANK });
    setErrors({});
  }
  function closeForm() {
    setEditing(null);
    setPendingDuplicate(null);
    setErrors({});
  }

  // `confirmDuplicate` is the second pass: the author has read the warning and
  // chosen to publish a near-identical update anyway, which is legitimate.
  async function save({ confirmDuplicate = false } = {}) {
    if (busy) return;                       // the Save button is disabled too; this
                                            // also covers Enter-key double submits
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) {
      setPendingDuplicate(null);
      return;
    }
    const payload = {
      version: (form.version || "").trim() || null,
      title: form.title.trim(),
      // Sent exactly as typed. The server counts its lines before it strips for
      // storage, so what it measures is what the form measured; the stored text
      // is still the trimmed one, unchanged from before.
      body: form.body,
      category: form.category,
      audience: form.audience,
      published: !!form.published,
      confirm_duplicate: confirmDuplicate,
    };
    setBusy(true);
    try {
      if (editing.id) await updateChangelogEntry(editing.id, payload);
      else await createChangelog(payload);
      // Only after the backend has confirmed the write — reporting success off a
      // click would tell the operator an update is live when the request failed.
      toastSuccess(
        editing.id
          ? "Update saved."
          : payload.published
            ? "Update published — it's on the What's New page now."
            : "Draft saved. It stays hidden until you publish it."
      );
      closeForm();
      await reload();
    } catch (e) {
      // A duplicate is not a failure, it's a question — so it gets the dialog
      // rather than an error toast. Keyed on the server's `code`, never on the
      // wording of the message.
      if (e.status === 409 && e.data && e.data.code === "duplicate_changelog") {
        setPendingDuplicate({ message: e.data.detail });
        return;
      }
      // The server measured the title and the body it actually received, and it
      // is the one that decides. If it rejected a field, that message goes ON
      // that field — a toast alone would leave the author looking for which box
      // is wrong, and would let the form keep claiming the value was fine.
      const perField = fieldErrors(e);
      if (Object.keys(perField).length) {
        setErrors((prev) => ({ ...prev, ...perField }));
        return;
      }
      toastError(e.message || "Couldn't save the entry.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!pendingDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteChangelogEntry(pendingDelete.id);
      setPendingDelete(null);
      toastSuccess("Update deleted.");
      await reload();
    } catch (e) {
      toastError(e.message || "Couldn't delete the entry.");
    } finally {
      setDeleting(false);
    }
  }

  // PUBLISHING is confirmed — it puts the entry on the What's New page of
  // everyone in its audience. Unpublishing only takes it back down, so that
  // stays immediate.
  // The patch is minimal: spreading the whole row sent server-owned fields
  // (created_at, published_at) straight back into the update.
  async function togglePublish(entry) {
    if (publishingId) return;
    if (!entry.published) { setPendingPublish(entry); return; }
    await applyPublish(entry, false);
  }

  async function applyPublish(entry, published) {
    setPublishingId(entry.id);
    try {
      await updateChangelogEntry(entry.id, {
        version: entry.version, title: entry.title, body: entry.body,
        category: entry.category, audience: entry.audience || "all", published,
        // This is a state flip on an entry that already exists — it is by
        // definition "the same title as itself", so the duplicate check must
        // not stand between an operator and taking something down.
        confirm_duplicate: true,
      });
      setPendingPublish(null);
      toastSuccess(published ? "Update published." : "Update unpublished.");
      await reload();
    } catch (err) {
      // Nothing to roll back: the switch renders from the server's `published`,
      // never from a local optimistic flag, so a failed call simply leaves the
      // row showing the state the server still holds.
      toastError(err.message || "Couldn't update the entry.");
    } finally {
      setPublishingId(null);
    }
  }

  // Measured on the same trimmed value the server measures, so the counter and
  // the Save button agree with the answer the API would give.
  const titleLength = (form.title || "").trim().length;
  const bodyLines = countLines(form.body || "");
  // Live, not only on submit: Save must be unavailable WHILE a field is over
  // the limit, rather than accepting the click and then explaining.
  const liveErrors = validate(form);
  const formInvalid = Object.keys(liveErrors).length > 0;

  return (
    <>
      <PageHeader title="Latest Updates" subtitle="Author product updates shown to users on the What's New page."
        actions={<><Button variant="outline" size="sm" onClick={reload}><RefreshCw size={16} /> Refresh</Button><Button size="sm" onClick={() => open(null)}><Plus size={16} /> New update</Button></>} />
      {/* dark:text-amber-200 — without it this banner kept text-amber-800 on a
          dark translucent panel and was effectively unreadable in dark mode. */}
      {demo && <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200"><WifiOff size={15} className="shrink-0" /> Demo data — backend not reachable.</div>}

      <LoadError message={error} onRetry={reload} />

      {loading ? <div className="flex justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div>
        : error ? null
        : rows.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400 dark:border-white/10">No updates yet.</div>
        : <>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xs">
              <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(ev) => { setQuery(ev.target.value); setPage(1); }}
                placeholder="Search title, body or version…"
                aria-label="Search updates"
                className="h-9 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATE_FILTERS.map((f) => (
                <button key={f.key} type="button"
                  onClick={() => { setStateFilter(f.key); setPage(1); }}
                  className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    stateFilter === f.key
                      ? "bg-brand-600 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10")}>
                  {f.label} <span className="opacity-70">({stateCounts[f.key] ?? 0})</span>
                </button>
              ))}
            </div>
          </div>
          {paged.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-400 dark:border-white/10">
              No update matches this search or filter.
            </div>
          ) : (
          <div className="grid gap-3">{paged.map((e) => {
            const rowBusy = publishingId === e.id;
            const action = e.published ? "Unpublish" : "Publish";
            return (
            <div key={e.id} className="card min-w-0 max-w-full p-4">
              <div className="flex items-start justify-between gap-3">
                {/* min-w-0 lets this column shrink below its content width, which
                    is what allows break-words below to actually take effect
                    instead of the card growing to fit one long word. */}
                <div className="min-w-0 max-w-full flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={categoryTone(e.category)}>{e.category || "update"}</Badge>
                    {e.version && <span className="text-xs text-slate-400">{e.version}</span>}
                    {e.published ? <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">published</Badge> : <Badge className="bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-400">draft</Badge>}
                    {e.audience && e.audience !== "all" && (
                      <Badge className="bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                        {audienceLabel(e.audience)} only
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 min-w-0 max-w-full break-words [overflow-wrap:anywhere] font-semibold text-slate-800 dark:text-white">{e.title}</p>
                  {/* "published" with no date told the operator nothing about
                      when it reached users. */}
                  {e.published && e.published_at && (
                    <p className="text-[0.6875rem] text-slate-400">Live since {fmtDate(e.published_at)}</p>
                  )}
                  {/* Same renderer as the What's New feed, so the two can't drift
                      apart again. Two lines here and no toggle: this is a list to
                      scan, and the full body is one click away in Edit. */}
                  <ExpandableText text={e.body} previewLines={2} className="mt-0.5 text-sm text-slate-500" />
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {/* An icon with no text needs an accessible name — Edit and
                      Delete announced themselves only as "button".
                      The megaphone is the ACTION (announce this / stop
                      announcing it). The old eye/crossed-eye drew the opposite
                      conclusion for everyone who read it as the current STATE,
                      which is why the control looked inverted — the backend flag
                      was right all along. */}
                  <button
                    type="button"
                    onClick={() => togglePublish(e)}
                    disabled={rowBusy}
                    title={`${action} this update`}
                    aria-label={`${action} ${e.title}`}
                    className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/10"
                  >
                    {rowBusy ? <Loader2 size={15} className="animate-spin" />
                      : e.published ? <MegaphoneOff size={15} /> : <Megaphone size={15} />}
                  </button>
                  <button type="button" onClick={() => open(e)} title="Edit update" aria-label={`Edit ${e.title}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"><Pencil size={15} /></button>
                  <button type="button" onClick={() => setPendingDelete(e)} title="Delete update" aria-label={`Delete ${e.title}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>);
          })}</div>
          )}
          {pages > 1 && (
            <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-400">
              <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
              <div className="flex items-center gap-3">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">‹ Prev</button>
                <span className="text-sm text-slate-500">Page {page} of {pages}</span>
                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">Next ›</button>
              </div>
            </div>
          )}
        </>}

      {/* Hidden while the duplicate question is up rather than stacked behind
          it: Modal traps Tab and listens for Escape on the document, so two of
          them open at once fight over the keyboard. The form state lives outside
          `editing`, so cancelling the question brings it back untouched. */}
      <Modal open={!!editing && !pendingDuplicate} onClose={closeForm} title={editing?.id ? "Edit update" : "New update"} size="md"
        footer={<div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={closeForm} disabled={busy}>Cancel</Button><Button size="sm" onClick={() => save()} disabled={busy || formInvalid}>{busy ? <Loader2 size={15} className="animate-spin" /> : "Save"}</Button></div>}>
        {/* One column on a phone, two where there's room — the fields used to be
            locked to grid-cols-2, which squeezed both controls to about half a
            phone's width each. */}
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Version" placeholder="v1.4 (optional)" maxLength={50} value={form.version || ""} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} />
            {/* No star on Category or Audience: both are <select>s that always
                hold a valid value, so neither can ever be left empty. */}
            <label className="block">
              <span className={FIELD_LABEL}>Category</span>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={SELECT_CLASS}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
          </div>

          <div>
            {/* The over-limit message shows as soon as the field is over, not
                only after a rejected Save: `errors.title` carries what the
                server said, `liveErrors.title` what the box says right now. */}
            <Input
              label="Title"
              required
              value={form.title}
              error={errors.title || (titleLength > MAX_TITLE ? liveErrors.title : undefined)}
              onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, title: v })); if (errors.title) setErrors((p) => ({ ...p, title: undefined })); }}
            />
            <p className={cn("mt-1 text-xs", titleLength > MAX_TITLE ? "font-medium text-rose-500" : "text-slate-400")}>
              {titleLength} / {MAX_TITLE}
            </p>
          </div>

          <label className="block">
            <span className={FIELD_LABEL}>Description<RequiredMark /></span>
            {(() => {
              // Over-limit shows while typing; `errors.body` is what the server
              // said and always wins over the local reading.
              const bodyError = errors.body || (bodyLines > MAX_BODY_LINES ? liveErrors.body : undefined);
              return (
                <>
                  <textarea
                    rows={6}
                    value={form.body}
                    aria-required="true"
                    aria-invalid={bodyError ? "true" : undefined}
                    onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, body: v })); if (errors.body) setErrors((p) => ({ ...p, body: undefined })); }}
                    className={cn(TEXTAREA_CLASS, bodyError && "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15")}
                  />
                  <span className="mt-1.5 flex flex-wrap items-baseline justify-between gap-2">
                    {bodyError
                      ? <span role="alert" className="text-xs font-medium text-rose-500">{bodyError}</span>
                      : <span className="text-xs text-slate-400">Line breaks are kept exactly as you type them.</span>}
                    <span className={cn("text-xs", bodyLines > MAX_BODY_LINES ? "font-medium text-rose-500" : "text-slate-400")}>
                      {bodyLines} / {MAX_BODY_LINES} lines
                    </span>
                  </span>
                </>
              );
            })()}
          </label>

          <label className="block">
            <span className={FIELD_LABEL}>Audience</span>
            <select value={form.audience} onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))} className={SELECT_CLASS}>
              {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <span className="mt-1.5 block text-xs text-slate-400">
              Who sees this on What&apos;s New. It changes visibility only — it grants nobody any extra access.
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="accent-brand-600" />
            Publish immediately
          </label>
        </div>
      </Modal>

      {/* A near-identical update is usually a double submit, occasionally a
          deliberate re-post. The server decides which entries collide and says
          so; the author decides whether to go ahead. */}
      <ConfirmDialog
        open={pendingDuplicate}
        title="This looks like an existing update"
        body={`${pendingDuplicate ? pendingDuplicate.message : ""} Posting it again will show two near-identical entries to the same people.`}
        confirmLabel="Post anyway"
        busy={busy}
        onConfirm={() => save({ confirmDuplicate: true })}
        onCancel={() => setPendingDuplicate(null)}
      />

      {/* Publishing puts this on the What's New page of everyone in its
          audience — worth one confirmation. Unpublishing is immediate. */}
      <ConfirmDialog
        open={pendingPublish}
        title={pendingPublish ? `Publish "${pendingPublish.title}"?` : ""}
        body={pendingPublish && pendingPublish.audience && pendingPublish.audience !== "all"
          ? `This appears on the What's New page for ${audienceLabel(pendingPublish.audience).toLowerCase()} straight away. You can unpublish it again at any time.`
          : "This appears on the What's New page for every user on the platform straight away. You can unpublish it again at any time."}
        confirmLabel="Publish"
        busy={!!publishingId}
        onConfirm={() => applyPublish(pendingPublish, true)}
        onCancel={() => setPendingPublish(null)}
      />

      <ConfirmDialog
        open={pendingDelete}
        title={pendingDelete ? `Delete ${pendingDelete.version || "this update"}?` : ""}
        body={pendingDelete && pendingDelete.published
          ? "This update is published — removing it takes it off the What's New page for every user. This cannot be undone."
          : "This removes the draft update permanently. This cannot be undone."}
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
